import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Moment from "react-moment";
import { toast } from "react-toastify";
import { getData } from "../../../Services/Services";
import { sopInstanceList, getThemeURL } from "../../../Services/EndPoints";

// ── Linked Modules (a.k.a. "Quick Links") ────────────────────────────────
// Panel-styled port of the QuickLinks section on the View Incident screen
// (GisincidentManagement/ViewIncident/IncidentInfo.js). Groups the incident's
// status history into workflow milestones; each sub-stage is a link that opens
// the module it points at (SOP instance list, resource assignment list, SOP
// instance detail for tasks, incident list), carrying the matched reference
// id(s) so the destination can bold the relevant rows.

const parseTime = (str) => (str ? new Date(str.replace(" ", "T")) : null);

const NAV_BY_REF_TYPE = {
  SOP_INSTANCE: {
    path: "/Disaster-acknowledgereport/view-sop/Sop-instance-list",
    stateKey: "highlightSopIds",
  },
  RESOURCE_DEPLOY: {
    path: "/Disaster-acknowledgereport/resource-assign-list",
    stateKey: "highlightDeployIds",
  },
  INCIDENT: {
    path: "/Disaster-acknowledgereport/situation-awareness/incident-management",
    stateKey: "highlightIncidentId",
    single: true,
  },
};

const SOP_INSTANCE_DETAIL_PATH =
  "/Disaster-acknowledgereport/view-sop/sops-instance";

const QUICK_LINK_GROUPS = [
  {
    label: "SOP & Resources Activated",
    links: [
      { label: "SOP Executed", ids: [946] },
      { label: "Resources Deployed", ids: [951] },
    ],
  },
  {
    label: "Task Execution",
    links: [
      { label: "Task In Progress", ids: [947] },
      { label: "Task Completed", ids: [948] },
    ],
  },
  {
    label: "SOP Outcome & Resources Released",
    links: [
      { label: "SOP Completed / Cancelled", ids: [949, 950] },
      { label: "Resources Released", ids: [952] },
    ],
  },
  {
    label: "Incident Closure",
    links: [
      { label: "SOP Closed", ids: [953] },
      { label: "Incident Closed", ids: [954] },
    ],
  },
];

const C = {
  surface: "#FFFFFF",
  primary: "#2A6591",
  primaryDark: "#1C4E72",
  text1: "#0F2D42",
  text2: "#3A5F78",
  text3: "#7A9BB0",
  border: "rgba(42,101,145,.12)",
};

const IncidentQuickLinks = ({ incident }) => {
  const navigate = useNavigate();

  const history = Array.isArray(incident?.incidentHistoryStatus)
    ? incident.incidentHistoryStatus
    : [];

  const milestones = useMemo(() => {
    if (history.length === 0) return [];
    return QUICK_LINK_GROUPS.map((group) => {
      const linkStates = group.links.map((link) => {
        const entries = history
          .filter((h) => link.ids.includes(h.status_id))
          .map((h) => ({ ...h, t: parseTime(h.created_on) }))
          .sort((a, b) => b.t - a.t);
        const achievedEntry = entries[0];
        const refType = achievedEntry?.reference_type;
        const nav = refType ? NAV_BY_REF_TYPE[refType] : null;
        const refIds = [
          ...new Set(
            entries
              .map((e) => e.reference_id)
              .filter((v) => v !== null && v !== undefined)
          ),
        ];
        return {
          ...link,
          achieved: !!achievedEntry,
          entry: achievedEntry,
          nav,
          refType,
          refIds,
        };
      });
      // Only surface links that have actually been reached — pending stages
      // are hidden entirely.
      const achievedLinks = linkStates.filter((l) => l.achieved);
      const achievedEntries = achievedLinks.map((l) => l.entry);
      const latest = achievedEntries.sort((a, b) => b.t - a.t)[0] || null;
      return {
        ...group,
        links: achievedLinks,
        done: achievedLinks.length > 0,
        latest,
      };
    }).filter((m) => m.done);
  }, [history]);

  const handleLinkClick = async (link) => {
    if (!link.achieved || link.refIds.length === 0) return;

    if (link.refType === "TASK_INSTANCE") {
      const sopIds = [
        ...new Set(
          history
            .filter((h) => h.reference_type === "SOP_INSTANCE")
            .map((h) => h.reference_id)
            .filter((v) => v !== null && v !== undefined)
        ),
      ].map((v) => String(v));

      let sopInstance = null;
      try {
        const profile = await getData(getThemeURL);
        const stateId = profile?.[0]?.state_id;
        const allInstances = await getData(`${sopInstanceList}/${stateId}`);
        sopInstance = (allInstances || []).find((si) =>
          sopIds.includes(String(si.id))
        );
      } catch (e) {
        // fall through to the id-only fallback below
      }

      if (!sopInstance && sopIds.length === 0) {
        toast.error("Could not resolve the SOP instance for this task.", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 2500,
        });
        return;
      }

      navigate(SOP_INSTANCE_DETAIL_PATH, {
        state: {
          data: sopInstance || { id: sopIds[0] },
          highlightTaskIds: link.refIds,
          sourceLabel: link.label,
          fromIncident: incident?.name || "",
        },
      });
      return;
    }

    if (!link.nav) return;
    navigate(link.nav.path, {
      state: {
        [link.nav.stateKey]: link.nav.single
          ? link.refIds[0] ?? incident?.id
          : link.refIds,
        sourceLabel: link.label,
        fromIncident: incident?.name || "",
      },
    });
  };

  const doneCount = milestones.filter((m) => m.done).length;
  if (milestones.length === 0 || doneCount === 0) return null;

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        borderTop: `1px solid rgba(42,101,145,.07)`,
        // Match the selected incident card's header/description tint.
        background: "linear-gradient(135deg, #EEF6FB 0%, #E4F0F8 100%)",
        padding: "10px 14px 12px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          marginBottom: 10,
        }}
      >
        <span style={{ fontSize: 12 }}>🔗</span>
        <span
          style={{
            fontFamily: "'Sora',sans-serif",
            fontSize: ".72rem",
            fontWeight: 700,
            color: C.primaryDark,
          }}
        >
          Linked Modules
        </span>
        <span
          style={{
            marginLeft: "auto",
            fontSize: ".55rem",
            fontWeight: 800,
            letterSpacing: ".03em",
            color: "#7C3AED",
            background: "#EDE9FE",
            padding: "2px 8px",
            borderRadius: 999,
          }}
        >
          WORKFLOW STAGES
        </span>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {milestones.map((m, idx) => (
          <div
            key={m.label}
            style={{
              display: "flex",
              gap: 9,
              padding: "8px 10px",
              borderRadius: 10,
              border: `1px solid ${C.border}`,
              background: C.surface,
            }}
          >
            <span
              style={{
                flexShrink: 0,
                width: 18,
                height: 18,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: ".6rem",
                fontWeight: 800,
                color: "#fff",
                background: m.done ? "#4ADE80" : C.text3,
              }}
            >
              {m.done ? "✓" : idx + 1}
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  fontSize: ".68rem",
                  fontWeight: 700,
                  color: C.text1,
                  lineHeight: 1.35,
                  marginBottom: 4,
                }}
              >
                {idx + 1}. {m.label}
              </div>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                {m.links.map((link, i) => {
                  const clickable =
                    link.achieved &&
                    link.refIds.length > 0 &&
                    (link.nav || link.refType === "TASK_INSTANCE");
                  return (
                    <React.Fragment key={link.label}>
                      {i > 0 && (
                        <span style={{ color: C.text3, fontSize: ".6rem" }}>
                          |
                        </span>
                      )}
                      <span
                        onClick={
                          clickable ? () => handleLinkClick(link) : undefined
                        }
                        onKeyDown={
                          clickable
                            ? (e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  handleLinkClick(link);
                                }
                              }
                            : undefined
                        }
                        role={clickable ? "button" : undefined}
                        tabIndex={clickable ? 0 : undefined}
                        title={
                          clickable ? `View ${link.label} record(s)` : undefined
                        }
                        style={{
                          fontSize: ".64rem",
                          fontWeight: 600,
                          color: clickable
                            ? C.primary
                            : link.achieved
                            ? C.text2
                            : C.text3,
                          cursor: clickable ? "pointer" : "default",
                          textDecoration: clickable ? "underline" : "none",
                          textUnderlineOffset: 2,
                          opacity: link.achieved ? 1 : 0.65,
                        }}
                      >
                        {link.label}
                        {clickable && (
                          <span style={{ fontSize: ".55rem", marginLeft: 2 }}>
                            ↗
                          </span>
                        )}
                      </span>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
            {m.latest && (
              <div
                style={{
                  flexShrink: 0,
                  fontSize: ".55rem",
                  fontWeight: 500,
                  color: C.text3,
                  textAlign: "right",
                  lineHeight: 1.4,
                }}
              >
                <Moment format="DD-MM-YYYY">{m.latest.created_on}</Moment>
                <br />
                <Moment format="HH:mm">{m.latest.created_on}</Moment>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default IncidentQuickLinks;
